import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Link, CircularProgress, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { getStoreDetail } from "../../Services/StoreService";
import NoImage from "../../assets/images/NoImage.webp";
import { getMarkerIcon } from "../common/map";
import { RateBadge } from "./StoreInfo";

// --- ユーティリティ ---

function formatDate(str) {
    if (!str) return '—';
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
}

// --- ヘルパーコンポーネント ---

function SectionBlock({ title, children, bgColor }) {
    return (
        <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 18, borderRadius: '2px', bgcolor: 'var(--accent)', flexShrink: 0 }} />
                <Typography
                    component="h3"
                    sx={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 15, fontWeight: 700,
                        color: 'var(--ink)', m: 0,
                    }}
                >
                    {title}
                </Typography>
            </Box>
            {bgColor ? (
                <Box sx={{ bgcolor: bgColor, borderRadius: '12px', p: 2 }}>
                    {children}
                </Box>
            ) : children}
        </Box>
    );
}

function SidebarLabel({ label, children }) {
    return (
        <Box sx={{ mb: 1.75 }}>
            <Box sx={{
                fontSize: 11, fontWeight: 700, color: 'var(--muted)',
                letterSpacing: '0.08em', mb: 0.5,
            }}>
                {label}
            </Box>
            <Box sx={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.6 }}>
                {children}
            </Box>
        </Box>
    );
}

function EatinTakeoutBadges({ value }) {
    const hasEatin = value === 1 || value === 3;
    const hasTakeout = value === 2 || value === 3;
    const badges = [
        hasEatin && { icon: '🪑', label: 'イートイン' },
        hasTakeout && { icon: '🛍', label: 'テイクアウト' },
    ].filter(Boolean);

    return (
        <Box sx={{ display: 'flex', gap: 0.75 }}>
            {badges.map(t => (
                <Box
                    key={t.label}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: '10px', py: '6px',
                        borderRadius: '8px',
                        fontSize: 12, fontWeight: 700,
                        bgcolor: 'var(--accent)',
                        color: 'white',
                    }}
                >
                    {t.icon} ◎ {t.label}
                </Box>
            ))}
        </Box>
    );
}

function MapBlock({ lat, lng, category, height = '160px' }) {
    const center = { lat: parseFloat(lat), lng: parseFloat(lng) };
    return (
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--line)', height }}>
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                zoom={17}
                options={{ gestureHandling: 'cooperative', clickableIcons: false, disableDefaultUI: true, zoomControl: true }}
            >
                <Marker
                    position={center}
                    icon={{
                        url: getMarkerIcon(category),
                        scaledSize: new window.google.maps.Size(32, 32),
                    }}
                />
            </GoogleMap>
        </Box>
    );
}

function PhotoGridPC({ images, selectedIndex, onSelect }) {
    const otherIndices = images.map((_, idx) => idx).filter(idx => idx !== selectedIndex);
    const slots = [selectedIndex, ...otherIndices].slice(0, 5);

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gridTemplateRows: '180px 180px',
            gap: '4px',
            height: 360,
        }}>
            {slots.map((origIdx, slotIdx) => {
                const src = images[origIdx] || null;
                const isSmall = slotIdx !== 0;
                return (
                    <Box
                        key={origIdx}
                        onClick={isSmall && src ? () => onSelect(origIdx) : undefined}
                        sx={{
                            gridRow: slotIdx === 0 ? 'span 2' : undefined,
                            bgcolor: 'var(--surface-2)',
                            overflow: 'hidden',
                            cursor: isSmall && src ? 'pointer' : 'default',
                            transition: 'opacity 0.15s',
                            '&:hover': isSmall && src ? { opacity: 0.8 } : {},
                        }}
                    >
                        {src && (
                            <img
                                src={src}
                                alt={`店舗写真 ${origIdx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

function ThumbnailStrip({ images, selectedIndex, onSelect }) {
    if (images.length <= 1) return null;
    return (
        <Box sx={{
            display: 'flex', gap: '8px',
            overflowX: 'auto', px: 1.5, py: 1.25,
            bgcolor: 'var(--surface-2)',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
        }}>
            {images.map((src, i) => (
                <Box
                    key={i}
                    onClick={() => onSelect(i)}
                    sx={{
                        width: 60, minWidth: 60, height: 44,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: selectedIndex === i
                            ? '2px solid var(--accent)'
                            : '2px solid transparent',
                        cursor: 'pointer',
                        bgcolor: 'var(--surface-2)',
                        opacity: selectedIndex === i ? 1 : 0.65,
                        transition: 'all 0.15s',
                    }}
                >
                    <img
                        src={src}
                        alt={`サムネイル ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </Box>
            ))}
        </Box>
    );
}

function DetailHeader({ name, category, onClose }) {
    return (
        <Box sx={{
            position: 'sticky', top: 0, zIndex: 10,
            bgcolor: 'var(--paper)', borderBottom: '1px solid var(--line)',
            px: { xs: 2, md: 4 }, py: '12px',
            display: 'flex', alignItems: 'center', gap: 2,
            flexShrink: 0,
        }}>
            <Box
                component="button"
                onClick={onClose}
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    border: 'none', bgcolor: 'transparent',
                    fontSize: 13, fontWeight: 600, color: 'var(--ink)',
                    cursor: 'pointer', fontFamily: 'inherit', p: 0,
                    '&:hover': { color: 'var(--accent)' },
                }}
            >
                ← 戻る
            </Box>
            <Typography sx={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {category} / {name}
            </Typography>
        </Box>
    );
}

function MobileHeader({ name, category, onClose }) {
    return (
        <Box sx={{
            px: 1.5, py: '10px',
            borderBottom: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'var(--paper)', flexShrink: 0,
        }}>
            <Box
                component="button"
                onClick={onClose}
                sx={{
                    width: 34, height: 34, minWidth: 34,
                    border: '1px solid var(--line)',
                    bgcolor: 'var(--paper)',
                    borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, cursor: 'pointer', color: 'var(--ink)',
                    fontFamily: 'inherit',
                    flexShrink: 0,
                }}
            >
                ←
            </Box>
            <Typography sx={{
                fontSize: 12, color: 'var(--muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {category} / {name}
            </Typography>
        </Box>
    );
}

function CommentText({ comment }) {
    if (!comment) return <Typography sx={{ fontSize: 13, color: 'var(--muted)' }}>—</Typography>;
    return (
        <Typography sx={{ fontSize: 13, lineHeight: 1.9, color: 'var(--ink)' }}>
            {comment.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i < comment.split('\n').length - 1 && <br />}</React.Fragment>
            ))}
        </Typography>
    );
}

// --- PC ビュー ---

function PCView({ detailInfo, images, onClose }) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailInfo.name + ', ' + detailInfo.address)}`;

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: 'var(--paper)' }}>
            <DetailHeader name={detailInfo.name} category={detailInfo.category} onClose={onClose} />
            <PhotoGridPC images={images} selectedIndex={selectedIndex} onSelect={setSelectedIndex} />

            <Box sx={{
                maxWidth: 1100, mx: 'auto',
                px: { xs: 2, md: 4 }, py: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 340px',
                gap: '40px',
                alignItems: 'start',
            }}>
                {/* 左カラム */}
                <Box>
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{
                            display: 'inline-block',
                            px: '10px', py: '3px', mb: 1,
                            bgcolor: 'var(--surface)', borderRadius: '999px',
                            fontSize: 11, fontWeight: 700, color: 'var(--accent)',
                        }}>
                            {detailInfo.category}
                        </Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 28, fontWeight: 700,
                                color: 'var(--ink)', m: '8px 0 12px', lineHeight: 1.3,
                            }}
                        >
                            {detailInfo.name}
                        </Typography>
                        <RateBadge rate={detailInfo.recommend} />
                    </Box>

                    <SectionBlock title="おすすめ商品">
                        <Typography sx={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.8 }}>
                            {detailInfo.product || '—'}
                        </Typography>
                    </SectionBlock>

                    <SectionBlock title="コメント">
                        <Box sx={{ p: 2.25, bgcolor: 'var(--hero)', borderRadius: '14px' }}>
                            <CommentText comment={detailInfo.comment} />
                        </Box>
                    </SectionBlock>
                </Box>

                {/* 右サイドバー */}
                <Box sx={{
                    position: 'sticky', top: 80,
                    bgcolor: 'var(--paper)',
                    border: '1px solid var(--line)',
                    borderRadius: '16px',
                    p: 2.5,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                }}>
                    <SidebarLabel label="住所">
                        {detailInfo.address || '—'}
                    </SidebarLabel>

                    <SidebarLabel label="カテゴリ">
                        {detailInfo.category}
                    </SidebarLabel>

                    <SidebarLabel label="ご利用スタイル">
                        <EatinTakeoutBadges value={detailInfo.eatin_takeout} />
                    </SidebarLabel>

                    <SidebarLabel label="ホームページ">
                        {detailInfo.url ? (
                            <Link
                                href={detailInfo.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'var(--accent)', fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}
                            >
                                🔗 {detailInfo.url}
                            </Link>
                        ) : '—'}
                    </SidebarLabel>

                    <SidebarLabel label="地図">
                        <Link
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'block', fontSize: 12, color: 'var(--accent)', mb: 0.75, fontWeight: 600 }}
                        >
                            Google マップで開く ↗
                        </Link>
                        <MapBlock
                            lat={detailInfo.lat} lng={detailInfo.lng}
                            category={detailInfo.category} height="160px"
                        />
                    </SidebarLabel>

                    <Box sx={{ pt: 1.5, borderTop: '1px solid var(--line)', textAlign: 'right', fontSize: 11, color: 'var(--muted)' }}>
                        最終更新日 {formatDate(detailInfo.updated_at)}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// --- モバイルビュー ---

function MobileView({ detailInfo, images, onClose }) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailInfo.name + ', ' + detailInfo.address)}`;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--paper)' }}>
            <MobileHeader name={detailInfo.name} category={detailInfo.category} onClose={onClose} />

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {/* ヒーロー画像 + カウンター */}
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ height: 260, overflow: 'hidden', bgcolor: 'var(--surface-2)' }}>
                        <img
                            src={images[selectedIndex]}
                            alt="店舗写真"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </Box>
                    <Box sx={{
                        position: 'absolute', right: 12, bottom: 12,
                        bgcolor: 'rgba(0,0,0,0.45)', color: 'white',
                        fontSize: 11, fontWeight: 700,
                        px: '10px', py: '3px', borderRadius: '999px',
                    }}>
                        {selectedIndex + 1} / {images.length}
                    </Box>
                </Box>

                {/* サムネイルストリップ */}
                <ThumbnailStrip images={images} selectedIndex={selectedIndex} onSelect={setSelectedIndex} />

                {/* テキストコンテンツ */}
                <Box sx={{ px: 2, pt: 2.25 }}>
                    {/* 店名 + カテゴリ + おすすめ度 */}
                    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: '1px solid var(--line)' }}>
                        <Box sx={{
                            display: 'inline-block',
                            px: '8px', py: '2px', mb: 1,
                            bgcolor: 'var(--surface)', borderRadius: '999px',
                            fontSize: 10, fontWeight: 700, color: 'var(--accent)',
                        }}>
                            {detailInfo.category}
                        </Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 22, fontWeight: 700,
                                color: 'var(--ink)', m: '0 0 10px', lineHeight: 1.3,
                            }}
                        >
                            {detailInfo.name}
                        </Typography>
                        <RateBadge rate={detailInfo.recommend} />
                    </Box>

                    {/* 住所 + ご利用スタイル */}
                    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: '1px solid var(--line)' }}>
                        <Box sx={{ mb: 1.5 }}>
                            <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', mb: 0.5 }}>
                                住所
                            </Box>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}>
                                {detailInfo.address || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', mb: 0.75 }}>
                                ご利用スタイル
                            </Box>
                            <EatinTakeoutBadges value={detailInfo.eatin_takeout} />
                        </Box>
                    </Box>

                    {/* おすすめ商品 */}
                    <SectionBlock title="おすすめ商品">
                        <Typography sx={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.8 }}>
                            {detailInfo.product || '—'}
                        </Typography>
                    </SectionBlock>

                    {/* コメント */}
                    <SectionBlock title="コメント">
                        <Box sx={{ p: 1.75, bgcolor: 'var(--hero)', borderRadius: '12px' }}>
                            <CommentText comment={detailInfo.comment} />
                        </Box>
                    </SectionBlock>

                    {/* 地図 */}
                    <SectionBlock title="地図">
                        <MapBlock
                            lat={detailInfo.lat} lng={detailInfo.lng}
                            category={detailInfo.category} height="200px"
                        />
                        <Box
                            component="button"
                            onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
                            sx={{
                                mt: 1.5, width: '100%', py: 1.5,
                                border: 'none',
                                bgcolor: 'var(--ink)', color: 'var(--paper)',
                                borderRadius: '10px',
                                fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', fontFamily: 'inherit',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            }}
                        >
                            📍 ルートを見る
                        </Box>
                    </SectionBlock>

                    {/* ホームページ */}
                    <SectionBlock title="ホームページ">
                        {detailInfo.url ? (
                            <Link
                                href={detailInfo.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    p: '12px 14px',
                                    bgcolor: 'var(--surface)', borderRadius: '10px',
                                    fontSize: 13, color: 'var(--accent)', fontWeight: 600,
                                    textDecoration: 'none', wordBreak: 'break-all',
                                }}
                            >
                                <span>🔗</span>
                                <span style={{ flex: 1 }}>{detailInfo.url}</span>
                                <span style={{ color: 'var(--muted)', fontSize: 12 }}>↗</span>
                            </Link>
                        ) : (
                            <Typography sx={{ fontSize: 13, color: 'var(--muted)' }}>—</Typography>
                        )}
                    </SectionBlock>
                </Box>

                {/* 最終更新日 */}
                <Box sx={{ py: 3, borderTop: '1px solid var(--line)', textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>
                    最終更新日 {formatDate(detailInfo.updated_at)}
                </Box>
            </Box>
        </Box>
    );
}

// --- メインコンポーネント ---

function DetailScreen({ id, onClose }) {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'));

    const [detailInfo, setDetailInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getStoreDetail(id).then(data => {
            setDetailInfo(data);
            setLoading(false);
        });
    }, [id]);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (loading) {
        return createPortal(
            <Box sx={{
                position: 'fixed', inset: 0, zIndex: 300,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'var(--paper)',
            }}>
                <CircularProgress size={28} sx={{ color: 'var(--accent)' }} />
            </Box>,
            document.body
        );
    }

    if (!detailInfo) {
        return createPortal(
            <Box sx={{
                position: 'fixed', inset: 0, zIndex: 300,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                bgcolor: 'var(--paper)',
            }}>
                <Typography sx={{ color: 'var(--muted)', mb: 2 }}>データが見つかりませんでした</Typography>
                <Button onClick={onClose} variant="outlined" sx={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}>
                    閉じる
                </Button>
            </Box>,
            document.body
        );
    }

    const images = (detailInfo.images && detailInfo.images.length > 0) ? detailInfo.images : [NoImage];

    return createPortal(
        <>
            {/* バックドロップ */}
            <Box
                onClick={onClose}
                sx={{ position: 'fixed', inset: 0, zIndex: 299, bgcolor: 'rgba(0,0,0,0.4)' }}
            />
            {/* パネル */}
            <Box
                onClick={e => e.stopPropagation()}
                sx={{
                    position: 'fixed', inset: 0, zIndex: 300,
                    bgcolor: 'var(--paper)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {isMd
                    ? <PCView detailInfo={detailInfo} images={images} onClose={onClose} />
                    : <MobileView detailInfo={detailInfo} images={images} onClose={onClose} />
                }
            </Box>
        </>,
        document.body
    );
}

export default DetailScreen;
